import { useState, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import { SUBSYSTEM_VISIBILITY_OPTIONS } from "../components/visualization/thermalHeatmapSubsystemConfig";

export type ThermalViewId = '3d' | 'charts' | 'margins';
export type ThermalViewMode = 'normal' | 'compact' | 'fullscreen';

export interface HeatmapLayerState {
  showGizmo: boolean;
  showNadir: boolean;
  showRollPitchYaw: boolean;
  showGrid: boolean;
  showDecks: boolean;
  showNodeTemp: boolean;
  showSubsystems: boolean;
  showSolarPanels: boolean;
  showAttitudeAnimation: boolean;
  syncCameras?: boolean;
}

export interface RenderingQualitySettings {
  dprIndex: 0 | 1 | 2;
  antialiasing: boolean;
  floorGridEnabled: boolean;
  refreshRateMs: 1000 | 10000;
}

export interface ChartDisplaySettings {
  showLegend: boolean;
  showOnlySubsystems: boolean;
  showEclipsePhases: boolean;
  showThresholdLines: boolean;
}

export interface SectionVisibilitySettings {
  showExternalFaces: boolean;
  showInternalDecks: boolean;
}

export interface PanelVisibilitySettings {
  showActiveView: boolean;
  showSubsystemCharts: boolean;
  showPerFace: boolean;
  showEnvironmental: boolean;
  showAlerts: boolean;
}

const VIEW_VISIBILITY_OPTION_ID: Record<ThermalViewId, string> = {
  '3d': 'threed_view_visible',
  charts: 'chart_view_visible',
  margins: 'margin_view_visible',
};

const VIEW_FILTERS_TITLE: Record<ThermalViewId, string> = {
  '3d': '3D Filters',
  charts: 'Chart Filters',
  margins: 'Margin Filters',
};

const SHARED_SECTIONS_OPTIONS: ThermalFilterOptionConfig[] = [
  { id: 'per_face_view', label: 'Per Face View' },
  { id: 'environmental_view', label: 'Environmental View' },
  { id: 'alerts_view', label: 'Alerts View' },
];

interface ThermalFilterOptionConfig {
  id: string;
  label: string;
  exclusiveGroup?: string;
  visibleWhen?: (ctx: { viewMode: ThermalViewMode; selectedOptionIdsByCategory: Record<string, string[]> }) => boolean;
  /** Disabled (and off) when the scenario has no attitude data */
  requiresAttitudeData?: boolean;
}

interface ThermalFilterCategoryConfig {
  id: string;
  name: string;
  options: ThermalFilterOptionConfig[];
  visibleWhen?: (ctx: { viewMode: ThermalViewMode; selectedOptionIdsByCategory: Record<string, string[]> }) => boolean;
}

interface ThermalFilterCategory {
  id: string;
  name: string;
  selected: boolean;
  options: Array<ThermalFilterOptionConfig & { checked: boolean; disabled: boolean }>;
}

const FACE_VIEW_GROUP = 'face_view';
const EXTERNAL_FACE_VIEW_ID = 'external_faces_3d';
const INTERNAL_FACE_VIEW_ID = 'internal_faces_3d';
const FACE_VIEW_IDS = [EXTERNAL_FACE_VIEW_ID, INTERNAL_FACE_VIEW_ID];

// Face views are radio-like in regular/compact, independent toggles in fullscreen (both views shown side by side)
const isExclusiveInMode = (opt: ThermalFilterOptionConfig, viewMode: ThermalViewMode) =>
  !!opt.exclusiveGroup && !(opt.exclusiveGroup === FACE_VIEW_GROUP && viewMode === 'fullscreen');

const THERMAL_FILTER_CATEGORIES: Record<ThermalViewId, ThermalFilterCategoryConfig[]> = {
  '3d': [
    {
      id: 'sections',
      name: 'Sections',
      options: [
        { id: VIEW_VISIBILITY_OPTION_ID['3d'], label: '3D View' },
        { id: EXTERNAL_FACE_VIEW_ID, label: 'External Faces', exclusiveGroup: FACE_VIEW_GROUP },
        { id: INTERNAL_FACE_VIEW_ID, label: 'Internal Faces', exclusiveGroup: FACE_VIEW_GROUP },
        ...SHARED_SECTIONS_OPTIONS,
      ],
    },
    {
      id: 'display_layers',
      name: 'Display Layers',
      options: [
        { id: 'orientation_gizmo', label: '3D Orientation Gizmo', exclusiveGroup: 'axis_indicator', visibleWhen: ({ viewMode }) => viewMode !== 'compact' },
        { id: 'nadir_axis', label: 'Nadir Axis', exclusiveGroup: 'axis_indicator' },
        { id: 'roll_pitch_yaw', label: 'RPY Axis', exclusiveGroup: 'axis_indicator' },
        { id: 'grid', label: 'Grid' },
        { id: 'decks', label: 'Deck Locations' },
        { id: 'node_temps', label: 'Node Temperatures' },
        { id: 'subsystems', label: 'Subsystems' },
        { id: 'solar_panels', label: 'Solar Panels' },
        { id: 'attitude_animation', label: 'Attitude Animation', requiresAttitudeData: true },
        { id: 'sync_cameras', label: 'Sync Cameras', visibleWhen: ({ viewMode, selectedOptionIdsByCategory }) =>
          viewMode === 'fullscreen' && FACE_VIEW_IDS.every((id) => (selectedOptionIdsByCategory['sections'] ?? []).includes(id)) },
      ],
    },
    {
      id: 'rendering_quality',
      name: 'Rendering Quality',
      options: [
        { id: 'refresh_5s', label: 'Low refresh rate', exclusiveGroup: 'refresh_rate' },
        { id: 'refresh_live', label: 'High refresh rate', exclusiveGroup: 'refresh_rate' },
        { id: 'dpr_low', label: 'Low quality', exclusiveGroup: 'dpr' },
        { id: 'dpr_medium', label: 'Default quality', exclusiveGroup: 'dpr' },
        { id: 'antialiasing', label: 'Anti-aliasing' },
        { id: 'floor_grid', label: 'Floor Grid' },
      ],
    },
  ],
  charts: [
    {
      id: 'sections',
      name: 'Sections',
      options: [
        { id: VIEW_VISIBILITY_OPTION_ID['charts'], label: 'Chart View', exclusiveGroup: 'chart_mode' },
        { id: 'subsystem_charts_view', label: 'Subsystem Charts View', exclusiveGroup: 'chart_mode', visibleWhen: ({ viewMode }) => viewMode === 'fullscreen' },
        ...SHARED_SECTIONS_OPTIONS,
      ],
    },
    {
      id: 'face_visibility',
      name: 'Face Visibility',
      visibleWhen: (ctx) => !(ctx.selectedOptionIdsByCategory['sections'] ?? []).includes('subsystem_charts_view'),
      options: [
        { id: 'PX', label: '+X' },
        { id: 'NX', label: '−X' },
        { id: 'PY', label: '+Y' },
        { id: 'NY', label: '−Y' },
        { id: 'PZ', label: '+Z' },
        { id: 'NZ', label: '−Z' },
        { id: 'INT1', label: 'Deck 1' },
        { id: 'INT2', label: 'Deck 2' },
      ],
    },
    {
      id: 'subsystem_visibility',
      name: 'Subsystem Visibility',
      visibleWhen: (ctx) => (ctx.selectedOptionIdsByCategory['sections'] ?? []).includes('subsystem_charts_view'),
      options: SUBSYSTEM_VISIBILITY_OPTIONS,
    },
    { id: 'chart_display', name: 'Chart Display', options: [
      { id: 'show_legend', label: 'Show Legend' },
      { id: 'show_eclipse_phases', label: 'Show Sunlit - Eclipse phases' },
      { id: 'show_threshold_lines', label: 'Show Threshold Lines' },
      { id: 'show_all_nodes', label: 'Show All Nodes', exclusiveGroup: 'node_display' },
      { id: 'show_only_subsystems', label: 'Show Only Subsystems', exclusiveGroup: 'node_display' },
    ] },
  ],
  margins: [
    {
      id: 'sections',
      name: 'Sections',
      options: [
        { id: VIEW_VISIBILITY_OPTION_ID['margins'], label: 'Margin View' },
        ...SHARED_SECTIONS_OPTIONS,
      ],
    },
    { id: 'section_visibility', name: 'Section Visibility', options: [
      { id: 'external_faces', label: 'External Faces' },
      { id: 'internal_decks', label: 'Internal Decks' },
    ] },
  ],
};

const DEFAULT_SELECTED_OPTIONS: Record<string, string[]> = {
  rendering_quality: ['refresh_live', 'dpr_medium', 'antialiasing', 'floor_grid'],
  display_layers: ['roll_pitch_yaw', 'grid', 'solar_panels', 'subsystems', 'sync_cameras', 'attitude_animation'],
  face_visibility: ['PX', 'NX', 'PY', 'NY', 'PZ', 'NZ', 'INT1', 'INT2'],
  chart_display: ['show_legend', 'show_eclipse_phases', 'show_threshold_lines', 'show_all_nodes'],
  section_visibility: ['external_faces', 'internal_decks'],
  sections: ['threed_view_visible', EXTERNAL_FACE_VIEW_ID, 'chart_view_visible', 'margin_view_visible', 'per_face_view', 'environmental_view', 'alerts_view'],
  subsystem_visibility: SUBSYSTEM_VISIBILITY_OPTIONS.map(o => o.id),
};

const useThermalFilters = (activeViewId: ThermalViewId, viewMode: ThermalViewMode, hasAttitudeData: boolean) => {
  const [active, setActive] = useState(false);
  const [selectedCategoryByView, setSelectedCategoryByView] = useState<Partial<Record<ThermalViewId, string>>>({});
  const [selectedOptionIdsByCategory, setSelectedOptionIdsByCategory] = useState<Record<string, string[]>>(DEFAULT_SELECTED_OPTIONS);

  // Entering fullscreen shows both face views; leaving keeps the single view on (External if none or both)
  const prevViewModeRef = useRef<ThermalViewMode | null>(null);
  useLayoutEffect(() => {
    const prevViewMode = prevViewModeRef.current;
    prevViewModeRef.current = viewMode;
    if (prevViewMode === viewMode) return;

    if (viewMode === 'fullscreen') {
      setSelectedOptionIdsByCategory((prev) => {
        const current = prev['sections'] ?? [];
        return { ...prev, sections: [...current.filter((id) => !FACE_VIEW_IDS.includes(id)), ...FACE_VIEW_IDS] };
      });
    } else if (prevViewMode === 'fullscreen') {
      setSelectedOptionIdsByCategory((prev) => {
        const current = prev['sections'] ?? [];
        const activeFaceViews = FACE_VIEW_IDS.filter((id) => current.includes(id));
        const keep = activeFaceViews.length === 1 ? activeFaceViews[0] : EXTERNAL_FACE_VIEW_ID;
        const sections = Array.from(new Set([...current.filter((id) => !FACE_VIEW_IDS.includes(id)), keep, VIEW_VISIBILITY_OPTION_ID['3d']]));
        return { ...prev, sections };
      });
    }
  }, [viewMode]);

  const categoryConfigs = useMemo(() => THERMAL_FILTER_CATEGORIES[activeViewId], [activeViewId]);

  const ctx = { viewMode, selectedOptionIdsByCategory };
  const visibleCategoryConfigs = useMemo(
    () => categoryConfigs.filter((cat) => cat.visibleWhen?.(ctx) ?? true),
    [categoryConfigs, viewMode, selectedOptionIdsByCategory],
  );
  const rawSelectedCategoryId = selectedCategoryByView[activeViewId] ?? visibleCategoryConfigs[0]?.id;
  const selectedCategoryId = visibleCategoryConfigs.some((c) => c.id === rawSelectedCategoryId)
    ? rawSelectedCategoryId
    : visibleCategoryConfigs[0]?.id;

  const categories = useMemo(
    () => {
      const selectedOptions = selectedOptionIdsByCategory;
      return visibleCategoryConfigs.map((cfg) => ({
        id: cfg.id,
        name: cfg.name,
        selected: cfg.id === selectedCategoryId,
        options: cfg.options
          .filter((opt) => opt.visibleWhen?.(ctx) ?? true)
          .map((opt) => {
            const disabled = !!opt.requiresAttitudeData && !hasAttitudeData;
            return {
              ...opt,
              disabled,
              checked: !disabled && (selectedOptions[cfg.id] ?? []).includes(opt.id),
            };
          }),
      }));
    },
    [visibleCategoryConfigs, selectedCategoryId, selectedOptionIdsByCategory, viewMode, hasAttitudeData],
  );

  const layerState = useMemo(() => {
    const selectedIds = selectedOptionIdsByCategory['display_layers'] ?? DEFAULT_SELECTED_OPTIONS['display_layers'] ?? [];
    return {
      showGizmo: selectedIds.includes('orientation_gizmo'),
      showNadir: selectedIds.includes('nadir_axis'),
      showRollPitchYaw: selectedIds.includes('roll_pitch_yaw'),
      showGrid: selectedIds.includes('grid'),
      showDecks: selectedIds.includes('decks'),
      showNodeTemp: selectedIds.includes('node_temps'),
      showSubsystems: selectedIds.includes('subsystems'),
      showSolarPanels: selectedIds.includes('solar_panels'),
      syncCameras: selectedIds.includes('sync_cameras'),
      showAttitudeAnimation: hasAttitudeData && selectedIds.includes('attitude_animation'),
    };
  }, [selectedOptionIdsByCategory, hasAttitudeData]);

  const renderingSettings = useMemo(() => {
    const selectedIds = selectedOptionIdsByCategory['rendering_quality'] ?? DEFAULT_SELECTED_OPTIONS['rendering_quality'] ?? [];
    return {
      dprIndex: selectedIds.includes('dpr_low') ? 0 : 1,
      antialiasing: selectedIds.includes('antialiasing'),
      floorGridEnabled: selectedIds.includes('floor_grid'),
      refreshRateMs: selectedIds.includes('refresh_5s') ? 10000 : 1000,
    } as RenderingQualitySettings;
  }, [selectedOptionIdsByCategory]);

  const visibleFaces = useMemo(() => {
    const selectedIds = selectedOptionIdsByCategory['face_visibility'] ?? DEFAULT_SELECTED_OPTIONS['face_visibility'] ?? [];
    return selectedIds;
  }, [selectedOptionIdsByCategory]);

  const visibleSubsystems = useMemo(() => {
    const selectedIds = selectedOptionIdsByCategory['subsystem_visibility'] ?? DEFAULT_SELECTED_OPTIONS['subsystem_visibility'] ?? [];
    return selectedIds;
  }, [selectedOptionIdsByCategory]);

  const chartDisplaySettings = useMemo(() => {
    const selectedIds = selectedOptionIdsByCategory['chart_display'] ?? DEFAULT_SELECTED_OPTIONS['chart_display'] ?? [];
    return {
      showLegend: selectedIds.includes('show_legend'),
      showOnlySubsystems: selectedIds.includes('show_only_subsystems'),
      showEclipsePhases: selectedIds.includes('show_eclipse_phases'),
      showThresholdLines: selectedIds.includes('show_threshold_lines'),
    };
  }, [selectedOptionIdsByCategory]);

  const sectionVisibilitySettings = useMemo(() => {
    const selectedIds = selectedOptionIdsByCategory['section_visibility'] ?? DEFAULT_SELECTED_OPTIONS['section_visibility'] ?? [];
    return {
      showExternalFaces: selectedIds.includes('external_faces'),
      showInternalDecks: selectedIds.includes('internal_decks'),
    };
  }, [selectedOptionIdsByCategory]);

  const panelVisibility = useMemo<PanelVisibilitySettings>(() => {
    const selectedIds = selectedOptionIdsByCategory['sections'] ?? DEFAULT_SELECTED_OPTIONS['sections'] ?? [];
    return {
      // Fullscreen 3D with both face views off hides the whole 3D block, same as unchecking 3D View
      showActiveView: selectedIds.includes(VIEW_VISIBILITY_OPTION_ID[activeViewId])
        && (activeViewId !== '3d' || viewMode !== 'fullscreen' || selectedIds.includes(EXTERNAL_FACE_VIEW_ID) || selectedIds.includes(INTERNAL_FACE_VIEW_ID)),
      showSubsystemCharts: selectedIds.includes('subsystem_charts_view'),
      showPerFace: selectedIds.includes('per_face_view'),
      showEnvironmental: selectedIds.includes('environmental_view'),
      showAlerts: selectedIds.includes('alerts_view'),
    };
  }, [selectedOptionIdsByCategory, activeViewId, viewMode]);

  const faceViews = useMemo(() => {
    const selectedIds = selectedOptionIdsByCategory['sections'] ?? DEFAULT_SELECTED_OPTIONS['sections'] ?? [];
    return {
      showExternal: selectedIds.includes(EXTERNAL_FACE_VIEW_ID),
      showInternal: selectedIds.includes(INTERNAL_FACE_VIEW_ID),
    };
  }, [selectedOptionIdsByCategory]);

  // In fullscreen, 3D View follows the face views: off when both are off, on when either is on
  const { showExternal: faceViewExternal, showInternal: faceViewInternal } = faceViews;
  useLayoutEffect(() => {
    if (viewMode !== 'fullscreen') return;
    const threedViewId = VIEW_VISIBILITY_OPTION_ID['3d'];
    const anyFaceViewOn = faceViewExternal || faceViewInternal;
    setSelectedOptionIdsByCategory((prev) => {
      const current = prev['sections'] ?? [];
      if (current.includes(threedViewId) === anyFaceViewOn) return prev;
      return {
        ...prev,
        sections: anyFaceViewOn ? [...current, threedViewId] : current.filter((id) => id !== threedViewId),
      };
    });
  }, [viewMode, faceViewExternal, faceViewInternal]);

  const viewFiltersTitle = VIEW_FILTERS_TITLE[activeViewId];

  const onActiveChange = useCallback(() => setActive((a) => !a), []);

  const selectCategory = useCallback(
    (categoryId: string) => {
      setSelectedCategoryByView((prev) => ({ ...prev, [activeViewId]: categoryId }));
    },
    [activeViewId],
  );

  const toggleOption = useCallback(
    (categoryId: string, optionId: string) => {
      setSelectedOptionIdsByCategory((prev) => {
        const currentSelected = prev[categoryId] ?? [];
        const option = categoryConfigs.find((cat) => cat.id === categoryId)?.options.find((opt) => opt.id === optionId);

        if (option && isExclusiveInMode(option, viewMode)) {
          // Keep non-exclusive options, replace exclusive group with the new selection
          const nonExclusiveOptions = currentSelected.filter((id) => {
            const opt = categoryConfigs.find((cat) => cat.id === categoryId)?.options.find((o) => o.id === id);
            return !opt?.exclusiveGroup;
          });
          return { ...prev, [categoryId]: [...nonExclusiveOptions, optionId] };
        } else {
          const isChecked = currentSelected.includes(optionId);
          let updated = isChecked ? currentSelected.filter((id) => id !== optionId) : [...currentSelected, optionId];

          // Enabling 3D View with no face view on: turn on External (regular/compact) or both (fullscreen)
          if (categoryId === 'sections' && optionId === VIEW_VISIBILITY_OPTION_ID['3d'] && !isChecked && !FACE_VIEW_IDS.some((id) => updated.includes(id))) {
            updated = [...updated, ...(viewMode === 'fullscreen' ? FACE_VIEW_IDS : [EXTERNAL_FACE_VIEW_ID])];
          }
          return { ...prev, [categoryId]: updated };
        }
      });
    },
    [categoryConfigs, viewMode],
  );

  const toggleAllOptions = useCallback(
    (categoryId: string) => {
      setSelectedOptionIdsByCategory((prev) => {
        const currentSelected = prev[categoryId] ?? [];
        const category = categoryConfigs.find((cat) => cat.id === categoryId);
        if (!category) return prev;

        const nonExclusiveOptions = category.options
          .filter((opt) => opt.visibleWhen?.({ viewMode, selectedOptionIdsByCategory: prev }) ?? true)
          .filter((opt) => !isExclusiveInMode(opt, viewMode))
          .map((opt) => opt.id);

        const allNonExclusiveChecked = nonExclusiveOptions.every((id) => currentSelected.includes(id));
        const updated = allNonExclusiveChecked
          ? currentSelected.filter((id) => !nonExclusiveOptions.includes(id))
          : Array.from(new Set([...currentSelected, ...nonExclusiveOptions]));

        return { ...prev, [categoryId]: updated };
      });
    },
    [categoryConfigs, viewMode],
  );

  const setFaceVisibility = useCallback(
    (faceIds: string[]) => {
      setSelectedOptionIdsByCategory((prev) => ({ ...prev, face_visibility: faceIds }));
    },
    [],
  );

  const setChartMode = useCallback(
    (mode: 'chart' | 'subsystem') => {
      setSelectedOptionIdsByCategory((prev) => {
        const target = mode === 'subsystem' ? 'subsystem_charts_view' : VIEW_VISIBILITY_OPTION_ID['charts'];
        const rest = (prev['sections'] ?? []).filter((id) => id !== 'subsystem_charts_view' && id !== VIEW_VISIBILITY_OPTION_ID['charts']);
        return { ...prev, sections: [...rest, target] };
      });
    },
    [],
  );

  const showSubsystems = useCallback(
    (optionIds: string[]) => {
      setSelectedOptionIdsByCategory((prev) => ({
        ...prev,
        subsystem_visibility: Array.from(new Set([...(prev['subsystem_visibility'] ?? []), ...optionIds])),
      }));
    },
    [],
  );

  return { active, placement: 'bottom' as const, categories, layerState, faceViews, renderingSettings, visibleFaces, chartDisplaySettings, sectionVisibilitySettings, panelVisibility, viewFiltersTitle, onActiveChange, selectCategory, toggleOption, toggleAllOptions, setFaceVisibility, setChartMode, showSubsystems, visibleSubsystems, showSubsystemCharts: panelVisibility.showSubsystemCharts };
};

export default useThermalFilters;
